'use client';

import dynamic from 'next/dynamic';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { AlertCircle, Play } from 'lucide-react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExtractForm } from '@/components/ExtractForm';
import { PageSection, SectionPill, SurfacePanel } from '@/components/site/page-shell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { homepageUseCaseFlows } from '@/content/homepage';
import { useWebSocket } from '@/hooks/useWebSocket';
import { isScreenshotLoadingState, shouldShowExtractionStatusPanel } from '@/lib/extractionStatus';
import { fetchRunStatus, issueWebsocketToken, isTerminalExtractStatus, type ExtractRunSession } from '@/services/api';

const ResultDisplay = dynamic(
  () => import('@/components/ResultDisplay').then((module) => module.ResultDisplay),
  {
    ssr: false,
    loading: () => (
      <div className="p-6 text-sm text-[var(--site-ink-muted)]">
        Загружаем результат...
      </div>
    ),
  },
);

const heroFieldPreview = homepageUseCaseFlows[0].fields.slice(0, 3);

type ScraperExperienceProps = {
  children: ReactNode;
  turnstileSiteKey?: string | null;
  turnstileBypass?: boolean;
};

type ScraperExperienceContextValue = {
  errorMessage: ReactNode;
  extractionComplete: boolean;
  handleExtractFailed: (message?: string) => void;
  handleExtractStart: (run: ExtractRunSession | null) => void;
  isPending: boolean;
  isProcessing: boolean;
  isScreenshotLoading: boolean;
  json: unknown;
  manualError: string | null;
  screenshotUrl: string | null;
  shouldShowStatusPanel: boolean;
  turnstileBypass?: boolean;
  turnstileSiteKey?: string | null;
  waitingForResults: boolean;
};

const ScraperExperienceContext = createContext<ScraperExperienceContextValue | null>(null);

function useScraperExperience() {
  const context = useContext(ScraperExperienceContext);
  if (!context) {
    throw new Error('useScraperExperience must be used within ScraperExperience');
  }
  return context;
}

export function ScraperExperience({
  children,
  turnstileSiteKey,
  turnstileBypass,
}: ScraperExperienceProps) {
  const [runSession, setRunSession] = useState<ExtractRunSession | null>(null);
  const [isPending, setIsPending] = useState(false);
  const { isLoading, screenshot_url, json, status, error } = useWebSocket(runSession);
  const [isMounted, setIsMounted] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(true);
  const [manualError, setManualError] = useState<string | null>(null);
  const [waitingForResults, setWaitingForResults] = useState(false);
  const [currentRunQuery, setCurrentRunQuery] = useState<string | null>(null);

  const updateRunQuery = useCallback((runId: string | null) => {
    if (typeof window === 'undefined') {
      return;
    }

    const nextUrl = new URL(window.location.href);
    if (runId) {
      nextUrl.searchParams.set('run', runId);
    } else {
      nextUrl.searchParams.delete('run');
    }

    const currentUrl = new URL(window.location.href);
    if (nextUrl.search === currentUrl.search) {
      setCurrentRunQuery(nextUrl.searchParams.get('run'));
      return;
    }

    window.history.replaceState(null, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    setCurrentRunQuery(nextUrl.searchParams.get('run'));
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (runSession) {
      setExtractionComplete(false);
    }
  }, [runSession]);

  useEffect(() => {
    if (json && !isLoading) {
      setExtractionComplete(true);
    }
  }, [json, isLoading]);

  useEffect(() => {
    if (!isLoading && status === 'closed') {
      setExtractionComplete(true);
    }
  }, [isLoading, status]);

  const handleExtractFailed = useCallback((message?: string) => {
    setIsPending(false);
    setExtractionComplete(true);
    if (message) {
      setManualError(message);
    }
  }, []);

  useEffect(() => {
    if (error) {
      handleExtractFailed();
    }
  }, [error, handleExtractFailed]);

  const handleExtractStart = useCallback((newRun: ExtractRunSession | null) => {
    if (newRun === null) {
      setRunSession(null);
      setIsPending(true);
      setExtractionComplete(false);
      setManualError(null);
      setWaitingForResults(true);
      updateRunQuery(null);
      return;
    }

    setRunSession(null);
    setManualError(null);

    setTimeout(() => {
      setRunSession(newRun);
      setIsPending(false);
      updateRunQuery(newRun.runId);
    }, 50);
  }, [updateRunQuery]);

  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') {
      return;
    }

    const syncRunQuery = () => {
      const params = new URLSearchParams(window.location.search);
      setCurrentRunQuery(params.get('run'));
    };

    syncRunQuery();
    window.addEventListener('popstate', syncRunQuery);
    return () => {
      window.removeEventListener('popstate', syncRunQuery);
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const runId = currentRunQuery;
    if (!runId || runSession?.runId === runId) {
      return;
    }

    let isCurrent = true;
    setManualError(null);
    setWaitingForResults(true);
    setIsPending(false);

    const rehydrate = async () => {
      try {
        let nextRun = await fetchRunStatus(runId);
        if (isCurrent && !isTerminalExtractStatus(nextRun.status)) {
          const token = await issueWebsocketToken(runId);
          nextRun = { ...nextRun, ...token };
        }

        if (!isCurrent) {
          return;
        }

        setRunSession(nextRun);
        setWaitingForResults(!isTerminalExtractStatus(nextRun.status) && !nextRun.json && !nextRun.error);
      } catch (rehydrationError) {
        if (!isCurrent) {
          return;
        }
        const message =
          rehydrationError instanceof Error
            ? rehydrationError.message
            : 'Не удалось восстановить состояние запуска';
        setRunSession(null);
        setManualError(message);
        setWaitingForResults(false);
        updateRunQuery(null);
      }
    };

    void rehydrate();
    return () => {
      isCurrent = false;
    };
  }, [currentRunQuery, isMounted, runSession?.runId, updateRunQuery]);

  const isProcessing = isLoading || isPending || status === 'connecting';
  const shouldShowStatusPanel = shouldShowExtractionStatusPanel(status, isPending, error, manualError);
  const isScreenshotLoading = isScreenshotLoadingState(isProcessing, screenshot_url);

  useEffect(() => {
    if (json || error || manualError || (!isProcessing && status === 'closed')) {
      setWaitingForResults(false);
    }
  }, [json, error, manualError, isProcessing, status]);

  const errorMessage = useMemo(() => {
    const activeError = error ?? manualError;
    if (activeError == null) {
      return null;
    }

    const normalizedError =
      activeError.trim() || 'Мы не смогли обработать запрос на извлечение. Попробуйте позже.';

    return (
      <Alert variant="destructive" data-testid="extraction-error">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{normalizedError}</AlertDescription>
      </Alert>
    );
  }, [error, manualError]);

  const value = useMemo<ScraperExperienceContextValue>(() => ({
    errorMessage,
    extractionComplete,
    handleExtractFailed,
    handleExtractStart,
    isPending,
    isProcessing,
    isScreenshotLoading,
    json,
    manualError,
    screenshotUrl: screenshot_url ?? null,
    shouldShowStatusPanel,
    turnstileBypass,
    turnstileSiteKey: turnstileSiteKey ?? null,
    waitingForResults,
  }), [
    errorMessage,
    extractionComplete,
    handleExtractFailed,
    handleExtractStart,
    isPending,
    isProcessing,
    isScreenshotLoading,
    json,
    manualError,
    screenshot_url,
    shouldShowStatusPanel,
    turnstileBypass,
    turnstileSiteKey,
    waitingForResults,
  ]);

  return (
    <ErrorBoundary>
      <ScraperExperienceContext.Provider value={value}>
        {children}
      </ScraperExperienceContext.Provider>
    </ErrorBoundary>
  );
}

export function ScraperExperiencePanel() {
  const {
    errorMessage,
    extractionComplete,
    handleExtractFailed,
    handleExtractStart,
    isPending,
    isProcessing,
    manualError,
    shouldShowStatusPanel,
    turnstileBypass,
    turnstileSiteKey,
    waitingForResults,
  } = useScraperExperience();

  return (
    <div id="live-extraction">
      <SurfacePanel className="gap-5 border-[color:rgba(255,255,255,0.62)] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(255,255,255,0.84))] p-[var(--site-home-hero-panel-pad)] shadow-[0_28px_80px_rgba(23,38,59,0.1)] max-sm:gap-4">
        <div className="space-y-3 max-sm:space-y-2.5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/76 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)] text-[length:var(--site-home-hero-flow-font-size)] font-semibold uppercase tracking-[0.16em] text-[var(--site-ink-strong)]">
            <Play className="h-[var(--site-home-hero-flow-icon-size)] w-[var(--site-home-hero-flow-icon-size)] fill-current" />
            Живое извлечение
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[length:var(--site-home-hero-flow-font-size)] font-semibold uppercase tracking-[0.16em] text-[var(--site-ink-subtle)] max-sm:gap-1.5">
            <span className="rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/82 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)]">
              URL страницы
            </span>
            <ArrowIcon />
            <span className="rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/82 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)]">
              Скриншот
            </span>
            <ArrowIcon />
            <span className="hidden rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/82 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)] sm:inline-flex">
              Поля / JSON
            </span>
          </div>
          <p className="max-w-2xl text-sm leading-6 text-[var(--site-ink-muted)]">
            Один запуск сохраняет источник, проверочный скриншот и структурированные веб-данные для шаблонов,
            датасетов и API.
          </p>
          <div className="flex flex-wrap gap-2">
            {heroFieldPreview.map((field, index) => (
              <SectionPill
                key={field}
                className={`${index > 1 ? 'hidden sm:inline-flex ' : ''}bg-white/82 px-[var(--site-home-hero-chip-pad-x)] py-[var(--site-home-hero-chip-pad-y)] text-[length:var(--site-home-hero-chip-font-size)]`}
              >
                {field}
              </SectionPill>
            ))}
          </div>
        </div>

        <ExtractForm
          onExtractStart={handleExtractStart}
          onExtractFailed={handleExtractFailed}
          isLoading={isProcessing}
          extractionComplete={extractionComplete}
          turnstileSiteKeyProp={turnstileSiteKey}
          turnstileBypassProp={turnstileBypass}
        />

        {errorMessage}

        {waitingForResults && !manualError ? (
          <div className="flex items-center gap-2 rounded-[1.5rem] border border-[color:var(--site-accent-border)] bg-[var(--site-accent-soft)] px-4 py-3 text-sm text-[var(--site-accent-text)] transition-opacity duration-300">
            <AlertCircle className="h-4 w-4" />
            <span>Ждем результаты извлечения...</span>
          </div>
        ) : null}

        {shouldShowStatusPanel ? (
          <SurfacePanel
            tone="muted"
            className="items-center text-center"
            data-testid="extraction-status"
          >
            <div className="motion-safe:animate-pulse flex flex-col items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-[color:rgba(23,38,59,0.08)]" />
              <p className="font-medium text-[var(--site-ink-strong)]">
                {isPending ? 'Запускаем процесс извлечения...' : 'Обрабатываем ваш запрос...'}
              </p>
              <p className="max-w-md text-sm text-[var(--site-ink-muted)]">
                Это может занять немного времени в зависимости от сайта и структуры извлекаемых данных.
              </p>
            </div>
          </SurfacePanel>
        ) : null}
      </SurfacePanel>
    </div>
  );
}

export function ScraperExperienceResults() {
  const { isProcessing, isScreenshotLoading, json, screenshotUrl } = useScraperExperience();

  if (!screenshotUrl && !json && !isProcessing) {
    return null;
  }

  return (
    <PageSection className="gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">
          Результат запуска
        </h2>
        <p className="text-sm leading-6 text-[var(--site-ink-muted)]">
          Скриншот страницы и структурированный результат остаются рядом, чтобы ускорить проверку.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.95fr_2.05fr]">
        <SurfacePanel className="hidden overflow-hidden p-0 lg:block">
          <ResultDisplay
            screenshotUrl={screenshotUrl}
            jsonData={null}
            isLoading={isScreenshotLoading}
            displayMode="screenshot"
          />
        </SurfacePanel>

        <SurfacePanel className="overflow-hidden p-0" data-testid="extraction-results">
          <ResultDisplay
            screenshotUrl={null}
            jsonData={json}
            isLoading={isProcessing && !json}
            displayMode="data"
          />
        </SurfacePanel>
      </div>
    </PageSection>
  );
}

function ArrowIcon() {
  return (
    <svg
      aria-hidden="true"
      className="hidden h-[var(--site-home-hero-flow-icon-size)] w-[var(--site-home-hero-flow-icon-size)] sm:block"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}
